import { Skeleton,Typography } from "@mui/material";
import { createTheme,ThemeProvider } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import MUIDataTable,{ MUIDataTableOptions } from "mui-datatables";
import { useContext,useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from "../../helpers/matomo";
import Aprovations from "../../service/Aprovations";
import Reports from "../../service/Reports";
import { aprovadosType,FilterAprovationsV2Type } from "../../types/aprovations/types";
import { OnDownloadCSV } from "../../types/helpers/types";
import type { ChecklistReportType } from "../../types/reports/types";
import ModalDetailPrint from "../Reports/Modal/ModalDetailPrint";
import { tableOptions } from "../TableHelpers/options";
import FiltersAprovationComponent from "./FiltersAprovationComponent";

const aprovationService = new Aprovations();
const reportsService = new Reports();

export default function History() {
  const { hierarchyLabel } = useContext(UserContext);
  const { t } = useTranslation("translation");

  const [dates, setDates] = useState({
    dataInicio: Date.now() - 2592000000,
    dataFim: Date.now(),
  });
  const [columnsViews, setColmnsViews] = useState<{ [k: string]: boolean }>();
  const [openModal, setOpenModal] = useState(false);
  const [selected, setSelected] = useState<ChecklistReportType>();
  const [filtersAprov, setFiltersAprov] = useState<FilterAprovationsV2Type>({
    inicio: {
      de: dates.dataInicio,
      ate: dates.dataFim,
    },
    aprovados: true,
    reprovados: true,
    // O histórico precisa consultar todos os formulários em que o usuário é aprovador.
    mostrarPendentesParaMim: false,
  });
  const [itensPerPage, setItensPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const [total, setTotal] = useState(0);
  const [firstRender, setFirstRender] = useState(true);
  const [pageCursors, setPageCursors] = useState<{ [page: number]: number | null }>({
    0: null,
  });

  useEffect(() => {
    const localColumnsViews = localStorage.getItem("columnsNotif");
    if (localColumnsViews) {
      setColmnsViews(JSON.parse(localColumnsViews));
    }
  }, []);

  const { data, isLoading, isFetching, isError, isSuccess, refetch } = useQuery({
    queryKey: ["GET_APROVATION_HISTORY", pageNumber, itensPerPage],
    queryFn: async () => {
      try {
        setFirstRender(false);
        const token = await AttToken();
        if (!token) throw new Error("Token not found");
        const currentLastIndex = pageCursors[pageNumber] ?? null;
        const response = await toast.promise(aprovationService.getAprovationByUserV2(token, filtersAprov, currentLastIndex, itensPerPage), {
          success: `${t("Aprovations.ready")}`,
          error: `${t("Aprovations.errorFetching")}`,
          pending: `${t("Aprovations.loading")}`,
        });
        if (!response) throw new Error("Response not found");
        const nextPage = pageNumber + 1;
        if (response?.lastIndex && !(nextPage in pageCursors)) {
          setPageCursors((prev) => ({
            ...prev,
            [nextPage]: response.lastIndex,
          }));
        }

        setTotal(response.total);
        return response;
      } catch (error) {
        throw new Error(`Erro ao buscar histórico de aprovações: ${error}`);
      }
    },
    enabled: false,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  useEffect(() => {
    if (!firstRender) {
      refetch();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, itensPerPage]);

  useEffect(() => {
    if (isSuccess) trackEventMatomo('histórico de aprovações', 'sucesso', 'carregamento', 'histórico carregado');
    if (isError) trackEventMatomo('histórico de aprovações', 'erro', 'carregamento', 'falha ao carregar histórico');
  }, [isError, isSuccess]);

  const getMuiTheme = () =>
    createTheme({
      components: {
        MuiFormGroup: {
          styleOverrides: {
            root: {
              "&>:nth-child(11)": {
                display: "none",
              },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            input: {
              width: "fit-content",
              margin: 0,
              padding: 0,
            },
          },
        },
        MuiList: {
          styleOverrides: {
            root: {
              display: "flex",
              flexDirection: "column",
            },
          },
        },
        MuiTableBody: {
          styleOverrides: {
            root: {
              td: {
                height: "fit-content",
                margin: 0,
                padding: "3px 10px",
                width: "fit-content",
              },
            },
          },
        },
        MuiGrid: {
          styleOverrides: {
            root: {
              margin: 0,
              padding: 0,
            },
          },
        },
        MuiFormControl: {
          styleOverrides: {
            root: {
              padding: "10px",
            },
          },
        },
      },
    });

  const columns = [
    {
      name: "key",
      label: `${t("checklists.printConfig.Inicio")}`,
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => {
          const newValue = new Date(value).toLocaleString();
          return <span style={{ width: "150px" }}>{newValue}</span>;
        },
      },
    },
    {
      name: "formNome",
      label: `${t("name")}`,
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
      },
    },
    {
      name: "nomeGerencia",
      label: hierarchyLabel.gerencia,
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
      },
    },
    {
      name: "nomeCliente",
      label: hierarchyLabel.cliente,
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
      },
    },
    {
      name: "nomeLocal",
      label: hierarchyLabel.local,
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
      },
    },
    {
      name: "nomeConjunto",
      label: hierarchyLabel.conjunto,
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
      },
    },
    {
      name: "nomeUnidade",
      label: hierarchyLabel.unidade,
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
      },
    },
    {
      name: "avaliador",
      label: `${t("Aprovations.approver")}`,
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true,
      },
    },
    {
      name: "status",
      label: `${t("Aprovations.status")}`,
      options: {
        customBodyRender: (value: string) =>
          value === "aprovado" ? (
            <span className="tag is-success">{t("ActionPlan.PlanosDeAcaoComp.approved")}</span>
          ) : (
            <span className="tag is-danger">{t("ActionPlan.PlanosDeAcaoComp.rejected")}</span>
          ),
      },
    },
    {
      name: "comentario",
      label: `${t("notifications.ticket.Comentario")}`,
      options: {},
    },
    {
      name: "key",
      label: `${t("checklists.printConfig.Inicio")}`,
      options: {
        display: false,
        filter: false,
        sort: false,
        sortThirdClickReset: false,
      },
    },
    {
      name: "usuario",
      label: `${t("checklists.printConfig.Inicio")}`,
      options: {
        display: false,
        filter: false,
        sort: false,
        sortThirdClickReset: false,
      },
    },
    {
      name: "aprovadores",
      label: `${t("checklists.printConfig.approvers")}`,
      options: {
        display: true,
        filter: false,
        sort: false,
        sortThirdClickReset: false,
        customBodyRender(value: string[]) {
          return value?.map((ele, index) => (
            <Typography fontSize={13} key={`${ele}|${index}`}>
              {ele},
            </Typography>
          ));
        },
      },
    },
    {
      name: "aprovados",
      label: "Aprovado por",
      options: {
        display: true,
        filter: false,
        sort: false,
        sortThirdClickReset: false,
        customBodyRender(value: aprovadosType[]) {
          return value?.map((ele) => (
            <Typography fontSize={13} key={ele.dataAvaliacao}>
              {ele.username},
            </Typography>
          ));
        },
      },
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    onRowClick: async (rowData) => {
      const timestamp = Number(rowData[10]);
      const user = rowData[11];

      if (timestamp && user) {
        const token = await AttToken();
        if (token) {
          const response = await reportsService.getDetailsById(token, [{ user, timestamp }]);
          const form = response?.data?.execucoes[0];

          if (form) {
            setSelected(form);
            setOpenModal(true);
          }
        }
      }
      trackEventMatomo("Histórico de aprovações", "click", "tr", "download");
    },
    onViewColumnsChange: (changedColumn: string, action: string) => {
      if (action === "remove") {
        setColmnsViews({
          ...columnsViews,
          [changedColumn]: false,
        });
        localStorage.setItem(
          "columnsNotif",
          JSON.stringify({
            ...columnsViews,
            [changedColumn]: false,
          }),
        );
      } else {
        setColmnsViews({
          ...columnsViews,
          [changedColumn]: true,
        });
        localStorage.setItem(
          "columnsNotif",
          JSON.stringify({
            ...columnsViews,
            [changedColumn]: false,
          }),
        );
      }
    },
    textLabels: {
      body: {
        noMatch: `${t("Aprovations.noApprovalsFound")}`,
        toolTip: `${t("table.toolTip")}`,
        columnHeaderTooltip: (column) => `${t("table.columnHeaderTooltip")} ${column.label}`,
      },
      pagination: {
        next: `${t("table.next")}`,
        previous: `${t("table.previous")}`,
        rowsPerPage: `${t("table.rowsPerPage")}`,
        displayRows: `${t("table.displayRows")}`,
      },
      toolbar: {
        search: `${t("table.search")}`,
        print: `${t("Aprovations.print")}`,
        filterTable: `${t("table.filterTable")}`,
        viewColumns: `${t("Aprovations.showHideColumns")}`,
      },
      filter: {
        title: `${t("table.title")}`,
        reset: `${t("table.reset")}`,
      },
      viewColumns: {
        title: `${t("table.viewColumnsTitle")}`,
      },
    },
    onDownload: (buildHead: Function, buildBody: Function, columns, data) => {
      trackEventMatomo("Aprovações pendentes", "click", "button", "download");
      const newData = data?.map((ele: OnDownloadCSV) => {
        ele.data[0] = new Date(ele?.data[0] as string).toLocaleString("pt-br").replace(",", "-");
        delete ele.data[10];
        delete columns[10];
        return ele;
      });
      return "\uFEFF" + buildHead(columns) + buildBody(newData);
    },
    onChangePage: (currentPage: number) => {
      setPageNumber(currentPage);
    },
    onChangeRowsPerPage: (numberOfRows: number) => {
      setPageNumber(0);
      setItensPerPage(numberOfRows);
      setPageCursors({ 0: null });
    },
    serverSide: true,
    rowsPerPageOptions: [10, 15, 50, 100],
    page: pageNumber,
    rowsPerPage: itensPerPage,
    count: total,
    download: true,
    viewColumns: true,
    print: true,
    search: false,
    sort: false,
  };

  useEffect(() => {
    setPageNumber(0);
    setPageCursors({ 0: null });
  }, [filtersAprov]);

  return (
    <>
      {openModal && selected && <ModalDetailPrint openModal={openModal} setOpenModal={setOpenModal} selected={[selected]} />}
      <FiltersAprovationComponent
        commingFromHistory={true}
        filter={filtersAprov}
        setFilter={setFiltersAprov}
        refetch={refetch}
        dates={dates}
        setDates={setDates}
      />
      {isLoading || isFetching ? (
        <Skeleton variant="rounded" height={400} width={"100%"} />
      ) : isError ? (
        <div>{t("Aprovations.errorFetching")}</div>
      ) : (
        !firstRender && (
          <>
            <ThemeProvider theme={getMuiTheme()}>
              <MUIDataTable
                title={"Histórico de aprovações"}
                data={data?.execucoes || []}
                columns={columns}
                options={{
                  ...tableOptions,
                  ...translatedTextLabels,
                }}
              />
            </ThemeProvider>
          </>
        )
      )}
    </>
  );
}
